import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import api from "../../services/api/axios";
import { BRAND_BLUE } from "../../theme/colors";

function currentMonthAiSpend(org, now = new Date()) {
  const start = org.ai_credits_month_start;
  if (!start) return 0;
  // Backend timestamps without an offset are UTC.
  const date = new Date(/(?:Z|[+-]\d{2}:\d{2})$/i.test(start) ? start : `${start}Z`);
  return date.getUTCFullYear() === now.getUTCFullYear() &&
    date.getUTCMonth() === now.getUTCMonth()
    ? Math.max(Number(org.ai_credits_used_this_month) || 0, 0)
    : 0;
}

function getToken() {
  try {
    return localStorage.getItem("access_token") || "";
  } catch {
    return "";
  }
}

function formatDollarsFromCents(value) {
  const cents = Math.max(Number(value) || 0, 0);
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  }).format(cents / 100);
}

function formatDollarsInputFromCents(value) {
  const cents = Math.max(Number(value) || 0, 0);
  return (cents / 100).toFixed(2);
}

function parseDollarsToCents(value) {
  if (value === "") {
    return null;
  }

  const dollars = Number(value);
  if (!Number.isFinite(dollars) || dollars < 0) {
    return 0;
  }

  return Math.round(dollars * 100);
}

export default function AdminAiCredits() {
  const token = getToken();

  const [orgs, setOrgs] = useState([]);
  const [orgAiLimitDrafts, setOrgAiLimitDrafts] = useState({});
  const [orgSeamlessLimitDrafts, setOrgSeamlessLimitDrafts] = useState({});
  const [savingOrgAiLimitId, setSavingOrgAiLimitId] = useState(null);
  const [savingOrgSeamlessLimitId, setSavingOrgSeamlessLimitId] = useState(null);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const totalMonthlySpendCents = orgs.reduce(
    (sum, org) => sum + currentMonthAiSpend(org),
    0,
  );
  const totalMonthlySeamlessCreditsUsed = orgs.reduce(
    (sum, org) => sum + Math.max(Number(org.seamless_credits_used_this_month) || 0, 0),
    0,
  );

  useEffect(() => {
    let cancelled = false;

    (async () => {
      if (!token) return;

      try {
        const res = await api.get("/admin/organizations", {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (cancelled) return;

        const rows = res.data || [];
        setOrgs(rows);
        setOrgAiLimitDrafts(
          Object.fromEntries(
            rows.map((org) => [
              org.id,
              org.monthly_ai_credit_limit == null
                ? ""
                : formatDollarsInputFromCents(org.monthly_ai_credit_limit),
            ]),
          ),
        );
        setOrgSeamlessLimitDrafts(
          Object.fromEntries(
            rows.map((org) => [org.id, org.monthly_seamless_credit_limit ?? ""]),
          ),
        );
      } catch (err) {
        if (cancelled) return;
        setError(
          err?.response?.data?.detail ||
            err?.message ||
            "Failed to load organizations.",
        );
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [token]);

  const saveOrgAiLimit = async (orgId) => {
    try {
      setSavingOrgAiLimitId(orgId);
      setError("");
      setSuccess("");

      const rawValue = orgAiLimitDrafts[orgId];
      const payload = {
        monthly_ai_credit_limit: parseDollarsToCents(rawValue),
      };

      const res = await api.patch(`/admin/organizations/${orgId}`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });

      setOrgs((prev) => prev.map((org) => (org.id === orgId ? res.data : org)));
      setOrgAiLimitDrafts((prev) => ({
        ...prev,
        [orgId]:
          res.data.monthly_ai_credit_limit == null
            ? ""
            : formatDollarsInputFromCents(res.data.monthly_ai_credit_limit),
      }));
      setSuccess(`Saved AI credit limit for "${res.data.name}".`);
    } catch (err) {
      setError(
        err?.response?.data?.detail ||
          err?.message ||
          "Failed to save AI credit limit.",
      );
    } finally {
      setSavingOrgAiLimitId(null);
    }
  };

  const saveOrgSeamlessLimit = async (orgId) => {
    try {
      setSavingOrgSeamlessLimitId(orgId);
      setError("");
      setSuccess("");

      const rawValue = orgSeamlessLimitDrafts[orgId];
      const payload = {
        monthly_seamless_credit_limit: rawValue === "" ? null : Number(rawValue),
      };

      const res = await api.patch(`/admin/organizations/${orgId}`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });

      setOrgs((prev) => prev.map((org) => (org.id === orgId ? res.data : org)));
      setOrgSeamlessLimitDrafts((prev) => ({
        ...prev,
        [orgId]: res.data.monthly_seamless_credit_limit ?? "",
      }));
      setSuccess(`Saved Seamless credit limit for "${res.data.name}".`);
    } catch (err) {
      setError(
        err?.response?.data?.detail ||
          err?.message ||
          "Failed to save Seamless credit limit.",
      );
    } finally {
      setSavingOrgSeamlessLimitId(null);
    }
  };

  return (
    <Box>
      <Typography variant="h6" gutterBottom sx={{ color: BRAND_BLUE }}>
        Manage AI Credits
      </Typography>

      <Typography variant="body2" sx={{ color: "text.secondary", mb: 2 }}>
        Limits apply per organization per calendar month and are shown in USD.
        Spend totals are based on the AI usage amount recorded by the backend.
        Seamless limits and usage are tracked separately as credits.
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

      <Paper
        elevation={2}
        sx={{
          p: 3,
          mb: 2,
          border: `1px solid ${BRAND_BLUE}`,
          borderRadius: 4,
          backgroundColor: "rgba(0, 78, 137, 0.04)",
        }}
      >
        <Typography variant="overline" sx={{ color: BRAND_BLUE }}>
          Monthly AI Spend
        </Typography>
        <Typography variant="h4" sx={{ color: BRAND_BLUE, fontWeight: 700 }}>
          {formatDollarsFromCents(totalMonthlySpendCents)}
        </Typography>
        <Typography variant="body2" sx={{ color: "text.secondary", mt: 0.5 }}>
          Total AI credit spend recorded this month across all organizations.
        </Typography>
      </Paper>

      <Paper
        elevation={2}
        sx={{
          p: 3,
          mb: 2,
          border: `1px solid ${BRAND_BLUE}`,
          borderRadius: 4,
          backgroundColor: "rgba(0, 78, 137, 0.04)",
        }}
      >
        <Typography variant="overline" sx={{ color: BRAND_BLUE }}>
          Monthly Seamless Usage
        </Typography>
        <Typography variant="h4" sx={{ color: BRAND_BLUE, fontWeight: 700 }}>
          {totalMonthlySeamlessCreditsUsed.toLocaleString()}
        </Typography>
        <Typography variant="body2" sx={{ color: "text.secondary", mt: 0.5 }}>
          Total Seamless credits used this month across all organizations.
        </Typography>
      </Paper>

      <Paper
        elevation={4}
        sx={{ p: 3, border: `2px solid ${BRAND_BLUE}`, borderRadius: 4 }}
      >
        <Typography variant="subtitle1" sx={{ mb: 2, color: BRAND_BLUE, fontWeight: 600 }}>
          Organization Credit Limits
        </Typography>
        <Stack spacing={1.5}>
          {orgs.map((org) => (
            <Stack
              key={org.id}
              direction={{ xs: "column", md: "row" }}
              spacing={2}
              sx={{ alignItems: { xs: "stretch", md: "flex-start" } }}
            >
              <Box sx={{ minWidth: 260 }}>
                <Typography sx={{ fontWeight: 600, color: BRAND_BLUE }}>
                  {org.name}
                </Typography>
                <Typography variant="caption" sx={{ color: BRAND_BLUE }}>
                  AI credits used this month (cents): {currentMonthAiSpend(org)}
                </Typography>
                <Typography display="block" variant="caption" sx={{ color: "text.secondary" }}>
                  Spend this month: {formatDollarsFromCents(currentMonthAiSpend(org))}
                </Typography>
                <Typography display="block" variant="caption" sx={{ color: "text.secondary" }}>
                  Seamless used this month: {org.seamless_credits_used_this_month ?? 0}
                </Typography>
              </Box>

              <Stack spacing={1.5} sx={{ width: { xs: "100%", md: 240 } }}>
                <TextField
                  label="AI monthly limit (USD)"
                  type="number"
                  value={orgAiLimitDrafts[org.id] ?? ""}
                  onChange={(e) =>
                    setOrgAiLimitDrafts((prev) => ({
                      ...prev,
                      [org.id]: e.target.value,
                    }))
                  }
                  inputProps={{ min: 0, step: "0.01" }}
                  helperText={`Current limit: ${formatDollarsFromCents(org.monthly_ai_credit_limit)}`}
                />

                <TextField
                  label="Seamless monthly limit (credits)"
                  type="number"
                  value={orgSeamlessLimitDrafts[org.id] ?? ""}
                  onChange={(e) =>
                    setOrgSeamlessLimitDrafts((prev) => ({
                      ...prev,
                      [org.id]: e.target.value,
                    }))
                  }
                  inputProps={{ min: 0, step: 1 }}
                  helperText={`Current limit: ${org.monthly_seamless_credit_limit ?? 0}`}
                />
              </Stack>

              <Stack spacing={1} sx={{ mt: { md: 1 } }}>
                <Button
                  variant="outlined"
                  onClick={() => saveOrgAiLimit(org.id)}
                  disabled={!token || savingOrgAiLimitId === org.id}
                >
                  {savingOrgAiLimitId === org.id ? "Saving..." : "Save AI Limit"}
                </Button>

                <Button
                  variant="outlined"
                  onClick={() => saveOrgSeamlessLimit(org.id)}
                  disabled={!token || savingOrgSeamlessLimitId === org.id}
                >
                  {savingOrgSeamlessLimitId === org.id ? "Saving..." : "Save Seamless Limit"}
                </Button>
              </Stack>
            </Stack>
          ))}
        </Stack>
      </Paper>
    </Box>
  );
}
